import html2canvas from "html2canvas";

// Copies must land in 1..20. 0 or garbage would print nothing, and a mistyped
// 9999 would run off a whole roll before anyone could reach Cancel.
export function clampCopies(value) {
  return Math.min(Math.max(Math.round(Number(value) || 1), 1), 20);
}

/**
 * Convert a rendered label DOM element into a TSPL print job.
 *
 * TSPL's native TEXT command uses the printer's resident fonts, which have
 * no Thai and no Chinese glyphs. So instead of emitting TEXT/BARCODE
 * commands we rasterise the exact HTML sticker to a 1-bit monochrome
 * bitmap and send it with a single BITMAP command — the label prints
 * pixel-for-pixel as designed (Thai, Chinese, QR, layout all preserved),
 * independent of what fonts the printer has.
 *
 * @param {HTMLElement} el         the .sticker element to print
 * @param {Object}      opts
 * @param {number}      opts.widthMm    label width in mm  (default 90)
 * @param {number}      opts.heightMm   label height in mm (default 50)
 * @param {number}      opts.dpi        printer resolution (default 203)
 * @param {number}      opts.gapMm      gap between labels in mm (default 2)
 * @param {number}      opts.direction  TSPL DIRECTION 0|1 (default 0)
 * @param {number}      opts.threshold  0-255 black cutoff (default 190)
 * @returns {Promise<Uint8Array>}  the full TSPL job, ready to write
 */
export async function stickerToTsplBytes(el, opts = {}) {
  const widthMm = Number(opts.widthMm) || 90;
  const heightMm = Number(opts.heightMm) || 50;
  const dpi = Number(opts.dpi) || 203;
  const gapMm = opts.gapMm != null ? Number(opts.gapMm) : 2;
  const direction = opts.direction ? 1 : 0;
  const threshold = opts.threshold != null ? Number(opts.threshold) : 190;

  // A layout wider than the print head (e.g. the 150x100 sticker on 100x150
  // media) feeds short-edge-first, so the image is turned a quarter turn and
  // the media dimensions are the layout's, swapped.
  const rotate90 = !!opts.rotate90;

  const dotsPerMm = dpi / 25.4;
  const layoutWidthDots = Math.round(widthMm * dotsPerMm);
  const layoutHeightDots = Math.round(heightMm * dotsPerMm);

  // What the printer is told, and what the bitmap must be.
  const mediaWidthMm = rotate90 ? heightMm : widthMm;
  const mediaHeightMm = rotate90 ? widthMm : heightMm;
  const widthDots = rotate90 ? layoutHeightDots : layoutWidthDots;
  const heightDots = rotate90 ? layoutWidthDots : layoutHeightDots;
  const widthBytes = Math.ceil(widthDots / 8);

  // Rasterise the sticker at printer resolution. CSS mm renders at 96dpi,
  // so scale up by dpi/96 to hit the printer's dot pitch.
  const source = await html2canvas(el, {
    scale: dpi / 96,
    backgroundColor: "#ffffff",
    logging: false,
    useCORS: true,
    onclone: (doc) => {
      // The on-screen sticker has a dashed preview border; drop it so it
      // doesn't rasterise into the label.
      doc.querySelectorAll(".sticker").forEach((s) => {
        s.style.border = "none";
        s.style.margin = "0";
      });
    },
  });

  // Normalise to the exact dot dimensions (html2canvas rounds differently).
  const canvas = document.createElement("canvas");
  canvas.width = widthDots;
  canvas.height = heightDots;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, widthDots, heightDots);
  if (rotate90) {
    // Quarter turn clockwise: the layout's (x,y) lands at (W-y, x), which maps
    // a WxH layout exactly onto this HxW bitmap.
    ctx.save();
    ctx.translate(widthDots, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(source, 0, 0, layoutWidthDots, layoutHeightDots);
    ctx.restore();
  } else {
    ctx.drawImage(source, 0, 0, widthDots, heightDots);
  }

  const { data } = ctx.getImageData(0, 0, widthDots, heightDots);

  // TSPL BITMAP mode: a bit value of 0 prints a black dot, 1 = no dot.
  // Start all-white (0xFF) and clear a bit wherever the pixel is dark.
  const bitmap = new Uint8Array(widthBytes * heightDots).fill(0xff);
  for (let y = 0; y < heightDots; y++) {
    const rowOffset = y * widthBytes;
    for (let x = 0; x < widthDots; x++) {
      const p = (y * widthDots + x) * 4;
      const alpha = data[p + 3];
      // Transparent pixels count as white (label background).
      const lum =
        alpha === 0
          ? 255
          : 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
      if (lum < threshold) {
        bitmap[rowOffset + (x >> 3)] &= ~(0x80 >> (x & 7));
      }
    }
  }

  // Darkness and speed are NOT sent by default. Setting DENSITY 10 turned the
  // printer DOWN — its own default is darker than that — and every label came
  // out faint where only the first had been. The printer knows its own paper
  // better than a guessed constant does, so it is left alone unless someone
  // deliberately overrides it with ?density= / ?speed= while tuning.
  const quality =
    (opts.density != null ? `DENSITY ${Number(opts.density)}\r\n` : "") +
    (opts.speed != null ? `SPEED ${Number(opts.speed)}\r\n` : "");

  const header =
    `SIZE ${mediaWidthMm} mm,${mediaHeightMm} mm\r\n` +
    `GAP ${gapMm} mm,0 mm\r\n` +
    quality +
    `DIRECTION ${direction}\r\n` +
    `CLS\r\n` +
    `BITMAP 0,0,${widthBytes},${heightDots},0,`;
  // One complete job per label: terminate the BITMAP data line, then a single
  // `PRINT 1,<copies>` — the printer makes the duplicates itself from one buffer
  // load. This is the pattern that printed large batches reliably; sending the
  // bitmap once and firing PRINT repeatedly (the old "copies" path) both stalled
  // this printer and produced only one copy, because the AS240 empties its image
  // buffer after each PRINT.
  const copies = clampCopies(opts.copies);
  const footer = `\r\nPRINT 1,${copies}\r\n`;

  const enc = new TextEncoder();
  const headerBytes = enc.encode(header);
  const footerBytes = enc.encode(footer);

  const job = new Uint8Array(
    headerBytes.length + bitmap.length + footerBytes.length
  );
  job.set(headerBytes, 0);
  job.set(bitmap, headerBytes.length);
  job.set(footerBytes, headerBytes.length + bitmap.length);
  return job;
}
